import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Avatar,
  AvatarGroup,
  IconButton,
  Menu,
  Skeleton,
  Tooltip,
  Fab,
  Stack,
  SwipeableDrawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Plus,
  Search,
  Filter,
  FolderKanban,
  Clock,
  Users,
  MoreVertical,
  Calendar,
  ListTodo,
  LayoutGrid,
  List,
  Kanban,
  GanttChart,
  BookTemplate,
  ChevronRight,
  X,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CheckCircle } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/store';
import { openModal } from '@/store/slices/uiSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { projectService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Project, ProjectStatus, ProjectType } from '@/types';
import { useFeaturePermission } from '@/shared/contexts/PermissionsContext';
import {
  ProjectKanbanBoard,
  ProjectGanttChart,
  ProjectTemplateModal,
  ProjectEditModal,
  DeleteProjectDialog,
} from '../components';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';

type ViewMode = 'grid' | 'list' | 'kanban' | 'gantt';

// Status colors
const statusColors: Record<ProjectStatus, string> = {
  [ProjectStatus.PLANNING]: '#64748b',
  [ProjectStatus.ACTIVE]: '#2563eb',
  [ProjectStatus.ON_HOLD]: '#f59e0b',
  [ProjectStatus.COMPLETED]: '#16a34a',
  [ProjectStatus.CANCELLED]: '#ef4444',
};

// Project type colors and labels
const projectTypeConfig: Record<ProjectType, { color: string; label: string }> = {
  [ProjectType.INTERNAL]: { color: '#6366f1', label: 'Internal' },
  [ProjectType.CUSTOMER]: { color: '#10b981', label: 'Customer' },
  [ProjectType.ONSITE]: { color: '#0ea5e9', label: 'Onsite' },
};

/**
 * ProjectsPage
 * List all projects with filtering and search
 * Mobile-first responsive design
 */
const ProjectsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const queryClient = useQueryClient();
  
  // Permission checks
  const canCreateProject = useFeaturePermission('projects', 'project_crud', 'create');
  const canEditProject = useFeaturePermission('projects', 'project_crud', 'edit');
  const canDeleteProject = useFeaturePermission('projects', 'project_crud', 'delete');
  
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  
  // Mobile states
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  
  // Modal states
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [cardMenuAnchor, setCardMenuAnchor] = useState<null | HTMLElement>(null);
  const [closeConfirmProject, setCloseConfirmProject] = useState<Project | null>(null);

  const closeProjectMutation = useMutation({
    mutationFn: (id: string) => projectService.updateProject(id, { status: ProjectStatus.COMPLETED }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      setCloseConfirmProject(null);
      setCardMenuAnchor(null);
    },
  });

  // Fetch projects
  const { data: projectsData, isLoading } = useQuery({
    queryKey: QUERY_KEYS.projects,
    queryFn: () => projectService.getProjects(),
  });

  const projects = projectsData?.items ?? [];

  // Filter projects
  const filteredProjects = projects.filter((project: Project) => {
    const matchesSearch =
      !searchQuery ||
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCreateProject = () => {
    dispatch(openModal({ type: 'projectCreate', data: {} }));
  };

  const formatDuration = (startDate: string, endDate: string) => {
    if (!startDate || !endDate || !dayjs(startDate).isValid() || !dayjs(endDate).isValid()) return '—';
    const days = dayjs(endDate).diff(dayjs(startDate), 'day');
    return `${days} days`;
  };

  const getProjectProgress = (project: Project) => {
    if (project.status === ProjectStatus.COMPLETED) return 100;
    if (project.completionPercentage != null && project.completionPercentage >= 0) return Math.min(100, Math.max(0, project.completionPercentage));
    if (!project.startDate || !project.endDate || !dayjs(project.startDate).isValid() || !dayjs(project.endDate).isValid()) return 0;
    const start = dayjs(project.startDate);
    const end = dayjs(project.endDate);
    const now = dayjs();
    if (now.isBefore(start)) return 0;
    if (now.isAfter(end)) return 100;
    const total = end.diff(start, 'day');
    const elapsed = now.diff(start, 'day');
    return Math.round((elapsed / total) * 100);
  };

  const getTotalTasks = (project: Project) => project.taskCount ?? project.taskIds?.length ?? 0;

  const getCardAccentColor = (project: Project) => project.color || statusColors[project.status];

  if (isLoading) {
    // Mobile loading skeleton
    if (isMobile) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={100} />
            ))}
          </Stack>
        </Box>
      );
    }
    
    // Desktop loading skeleton
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="rounded" height={200} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Mobile Header */}
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {/* Title Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" fontWeight={600}>
              Projects
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                sx={{ bgcolor: mobileSearchOpen ? 'primary.50' : 'transparent' }}
              >
                <Search size={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setMobileFilterOpen(true)}
                sx={{ 
                  bgcolor: statusFilter !== 'all' ? 'primary.50' : 'transparent',
                  color: statusFilter !== 'all' ? 'primary.main' : 'inherit',
                }}
              >
                <Filter size={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setTemplateModalOpen(true)}
              >
                <BookTemplate size={18} />
              </IconButton>
            </Box>
          </Box>
          
          {/* Mobile Search (Collapsible) */}
          {mobileSearchOpen && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <X size={14} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
          )}
          
          {/* View Mode Toggle (Mobile) */}
          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5, width: 'fit-content' }}>
            <IconButton
              onClick={() => setViewMode('grid')}
              size="small"
              sx={{
                p: 0.75,
                bgcolor: viewMode === 'grid' ? 'background.paper' : 'transparent',
                color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'grid' ? 1 : 0,
              }}
            >
              <LayoutGrid size={16} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              size="small"
              sx={{
                p: 0.75,
                bgcolor: viewMode === 'list' ? 'background.paper' : 'transparent',
                color: viewMode === 'list' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'list' ? 1 : 0,
              }}
            >
              <List size={16} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('kanban')}
              size="small"
              sx={{
                p: 0.75,
                bgcolor: viewMode === 'kanban' ? 'background.paper' : 'transparent',
                color: viewMode === 'kanban' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'kanban' ? 1 : 0,
              }}
            >
              <Kanban size={16} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <>
          {/* Desktop Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Projects
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track your projects
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canCreateProject && (
                <Button
                  variant="outlined"
                  startIcon={<BookTemplate size={16} />}
                  onClick={() => setTemplateModalOpen(true)}
                >
                  Templates
                </Button>
              )}
              {canCreateProject && (
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={handleCreateProject}
                >
                  New Project
                </Button>
              )}
            </Box>
          </Box>

          {/* Desktop Filters */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />

              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | 'all')}
                  displayEmpty
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value={ProjectStatus.PLANNING}>Planning</MenuItem>
                  <MenuItem value={ProjectStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={ProjectStatus.ON_HOLD}>On Hold</MenuItem>
                  <MenuItem value={ProjectStatus.COMPLETED}>Completed</MenuItem>
                  <MenuItem value={ProjectStatus.CANCELLED}>Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* View Toggle */}
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
              <Tooltip title="Grid View">
                <IconButton
                  onClick={() => setViewMode('grid')}
                  size="small"
                  sx={{
                    bgcolor: viewMode === 'grid' ? 'background.paper' : 'transparent',
                    color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                    boxShadow: viewMode === 'grid' ? 1 : 0,
                  }}
                >
                  <LayoutGrid size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="List View">
                <IconButton
                  onClick={() => setViewMode('list')}
                  size="small"
                  sx={{
                    bgcolor: viewMode === 'list' ? 'background.paper' : 'transparent',
                    color: viewMode === 'list' ? 'primary.main' : 'text.secondary',
                    boxShadow: viewMode === 'list' ? 1 : 0,
                  }}
                >
                  <List size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Kanban Board">
                <IconButton
                  onClick={() => setViewMode('kanban')}
                  size="small"
                  sx={{
                    bgcolor: viewMode === 'kanban' ? 'background.paper' : 'transparent',
                    color: viewMode === 'kanban' ? 'primary.main' : 'text.secondary',
                    boxShadow: viewMode === 'kanban' ? 1 : 0,
                  }}
                >
                  <Kanban size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Timeline View">
                <IconButton
                  onClick={() => setViewMode('gantt')}
                  size="small"
                  sx={{
                    bgcolor: viewMode === 'gantt' ? 'background.paper' : 'transparent',
                    color: viewMode === 'gantt' ? 'primary.main' : 'text.secondary',
                    boxShadow: viewMode === 'gantt' ? 1 : 0,
                  }}
                >
                  <GanttChart size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </>
      )}

      {/* View Content */}
      {viewMode === 'kanban' ? (
        <ProjectKanbanBoard
          projects={filteredProjects}
          isLoading={isLoading}
          onProjectClick={handleProjectClick}
          onEditProject={canEditProject ? (project) => {
            setSelectedProject(project);
            setEditModalOpen(true);
          } : undefined}
          onDeleteProject={canDeleteProject ? (project) => {
            setSelectedProject(project);
            setDeleteDialogOpen(true);
          } : undefined}
          onCloseProject={canEditProject ? (project) => setCloseConfirmProject(project) : undefined}
        />
      ) : viewMode === 'gantt' && !isMobile ? (
        <Card sx={{ height: 600, overflow: 'hidden' }}>
          <ProjectGanttChart
            projects={filteredProjects}
            isLoading={isLoading}
            onProjectClick={handleProjectClick}
          />
        </Card>
      ) : filteredProjects.length === 0 ? (
        <Card sx={{ borderRadius: { xs: 2, md: 1 } }}>
          <Box sx={{ py: { xs: 6, md: 8 }, px: 2, textAlign: 'center' }}>
            <FolderKanban size={isMobile ? 40 : 48} style={{ color: '#94a3b8', marginBottom: 16 }} />
            <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={500} color="text.secondary" gutterBottom>
              {projects.length === 0 ? 'No projects yet' : 'No projects match your filters'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {projects.length === 0
                ? 'Create your first project to get started'
                : 'Try adjusting your search or filters'}
            </Typography>
            {projects.length === 0 && !isMobile && canCreateProject && (
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={handleCreateProject}
              >
                Create Project
              </Button>
            )}
          </Box>
        </Card>
      ) : viewMode === 'grid' ? (
        // Grid View (Mobile: Full-width cards, Desktop: 3 columns)
        isMobile ? (
          <Stack spacing={1.5}>
            {filteredProjects.map((project: Project) => {
              const progress = getProjectProgress(project);
              
              return (
                <Card
                  key={project.id}
                  onClick={() => handleProjectClick(project.id)}
                  sx={{
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    borderLeft: `4px solid ${getCardAccentColor(project)}`,
                    '&:active': {
                      transform: 'scale(0.98)',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          bgcolor: `${getCardAccentColor(project)}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <FolderKanban size={18} style={{ color: getCardAccentColor(project) }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {project.projRef && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.25 }}>
                            {project.projRef}
                          </Typography>
                        )}
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {project.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                          <Chip
                            label={project.status.replace('_', ' ')}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              bgcolor: `${statusColors[project.status]}20`,
                              color: statusColors[project.status],
                              textTransform: 'capitalize',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          {project.projectType && (
                            <Chip
                              label={projectTypeConfig[project.projectType]?.label || project.projectType}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                bgcolor: `${projectTypeConfig[project.projectType]?.color || '#94a3b8'}20`,
                                color: projectTypeConfig[project.projectType]?.color || '#94a3b8',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          {canEditProject && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                            <Chip
                              icon={<CheckCircle size={12} />}
                              label="Close Project"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setCloseConfirmProject(project); }}
                              sx={{
                                height: 18,
                                fontSize: '0.6rem',
                                bgcolor: 'success.50',
                                color: 'success.main',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      {(canEditProject || canDeleteProject) && (
                        <IconButton
                          size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProject(project);
                          setCardMenuAnchor(e.currentTarget);
                        }}
                        sx={{ color: 'text.secondary', flexShrink: 0 }}
                      >
                        <MoreVertical size={18} />
                      </IconButton>
                      )}
                      <ChevronRight size={18} style={{ color: '#94a3b8', flexShrink: 0 }} />
                    </Box>

                    {/* Completion % */}
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Completion
                        </Typography>
                        <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.65rem' }}>
                          {progress}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 4,
                          borderRadius: 1,
                          bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getCardAccentColor(project),
                          },
                        }}
                      />
                    </Box>

                    {/* Footer */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Calendar size={12} style={{ color: '#94a3b8' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          Due {dayjs(project.endDate).format('MMM D')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ListTodo size={12} style={{ color: '#94a3b8' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {getTotalTasks(project)} tasks
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {filteredProjects.map((project: Project) => {
              const progress = getProjectProgress(project);

              return (
                <Grid item xs={12} sm={6} md={4} key={project.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      borderLeft: `4px solid ${getCardAccentColor(project)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      },
                    }}
                    onClick={() => handleProjectClick(project.id)}
                  >
                    <CardContent>
                      {/* Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: `${getCardAccentColor(project)}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FolderKanban
                              size={20}
                              style={{ color: getCardAccentColor(project) }}
                            />
                          </Box>
                          <Box>
                            {project.projRef && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                {project.projRef}
                              </Typography>
                            )}
                            <Typography variant="subtitle1" fontWeight={600}>
                              {project.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={project.status.replace('_', ' ')}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  bgcolor: `${statusColors[project.status]}20`,
                                  color: statusColors[project.status],
                                  textTransform: 'capitalize',
                                }}
                              />
                              {project.projectType && (
                                <Chip
                                  label={projectTypeConfig[project.projectType]?.label || project.projectType}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: `${projectTypeConfig[project.projectType]?.color || '#94a3b8'}20`,
                                    color: projectTypeConfig[project.projectType]?.color || '#94a3b8',
                                  }}
                                />
                              )}
                              {canEditProject && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                                <Chip
                                  icon={<CheckCircle size={12} />}
                                  label="Close Project"
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setCloseConfirmProject(project); }}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: 'success.50',
                                    color: 'success.main',
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        {(canEditProject || canDeleteProject) && (
                          <IconButton
                            size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProject(project);
                            setCardMenuAnchor(e.currentTarget);
                          }}
                          sx={{ color: 'text.secondary' }}
                        >
                          <MoreVertical size={16} />
                        </IconButton>
                        )}
                      </Box>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          minHeight: 40,
                        }}
                      >
                        {project.description || 'No description'}
                      </Typography>

                      {/* Progress */}
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Completion
                          </Typography>
                          <Typography variant="caption" fontWeight={500}>
                            {progress}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 6,
                            borderRadius: 1,
                            bgcolor: 'grey.100',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getCardAccentColor(project),
                            },
                          }}
                        />
                      </Box>

                      {/* Footer */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Calendar size={14} style={{ color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(project.endDate).format('MMM D, YYYY')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Users size={14} style={{ color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">
                            {getTotalTasks(project)} tasks
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )
      ) : (
        // List View
        isMobile ? (
          <Stack spacing={1}>
            {filteredProjects.map((project: Project) => {
              const progress = getProjectProgress(project);
              
              return (
                <Card
                  key={project.id}
                  onClick={() => handleProjectClick(project.id)}
                  sx={{
                    borderRadius: 2,
                    cursor: 'pointer',
                    borderLeft: `4px solid ${getCardAccentColor(project)}`,
                    '&:active': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: `${getCardAccentColor(project)}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FolderKanban size={16} style={{ color: getCardAccentColor(project) }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {project.projRef && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.25 }}>
                          {project.projRef}
                        </Typography>
                      )}
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {project.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {progress}% · {getTotalTasks(project)} tasks
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Due {dayjs(project.endDate).format('MMM D')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={project.status.split('_')[0]}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          bgcolor: `${getCardAccentColor(project)}20`,
                          color: getCardAccentColor(project),
                          textTransform: 'capitalize',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                      {canEditProject && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                        <Chip
                          icon={<CheckCircle size={12} />}
                          label="Close"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setCloseConfirmProject(project); }}
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            bgcolor: 'success.50',
                            color: 'success.main',
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      )}
                    </Box>
                    {(canEditProject || canDeleteProject) && (
                      <IconButton
                        size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProject(project);
                        setCardMenuAnchor(e.currentTarget);
                      }}
                      sx={{ color: 'text.secondary', flexShrink: 0 }}
                    >
                      <MoreVertical size={16} />
                    </IconButton>
                    )}
                    <ChevronRight size={16} style={{ color: '#94a3b8' }} />
                  </Box>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Card>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {filteredProjects.map((project: Project, index: number) => {
                const progress = getProjectProgress(project);

                return (
                  <Box
                    key={project.id}
                    onClick={() => handleProjectClick(project.id)}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      cursor: 'pointer',
                      borderLeft: `4px solid ${getCardAccentColor(project)}`,
                      borderBottom: index < filteredProjects.length - 1 ? '1px solid' : 'none',
                      borderBottomColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: `${getCardAccentColor(project)}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FolderKanban size={20} style={{ color: getCardAccentColor(project) }} />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      {project.projRef && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          {project.projRef}
                        </Typography>
                      )}
                      <Typography variant="subtitle2" fontWeight={600}>
                        {project.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.description?.slice(0, 100) || 'No description'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={project.status.replace('_', ' ')}
                        size="small"
                        sx={{
                          bgcolor: `${statusColors[project.status]}20`,
                          color: statusColors[project.status],
                          textTransform: 'capitalize',
                        }}
                      />
                      {canEditProject && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                        <Chip
                          icon={<CheckCircle size={12} />}
                          label="Close Project"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setCloseConfirmProject(project); }}
                          sx={{
                            bgcolor: 'success.50',
                            color: 'success.main',
                            '&:hover': { bgcolor: 'success.100' },
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ width: 100 }}>
                      <Typography variant="caption" color="text.secondary">
                        {progress}% complete
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 4,
                          borderRadius: 1,
                          '& .MuiLinearProgress-bar': { bgcolor: getCardAccentColor(project) },
                        }}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {getTotalTasks(project)} tasks
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      Due {dayjs(project.endDate).format('MMM D')}
                    </Typography>

                    {(canEditProject || canDeleteProject) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProject(project);
                          setCardMenuAnchor(e.currentTarget);
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Card>
        )
      )}

      {/* Project card actions menu */}
      {(canEditProject || canDeleteProject) && (
        <Menu
          anchorEl={cardMenuAnchor}
          open={Boolean(cardMenuAnchor)}
          onClose={() => setCardMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClick={() => setCardMenuAnchor(null)}
        >
          {canEditProject && (
            <MenuItem
              onClick={() => {
                setEditModalOpen(true);
                setCardMenuAnchor(null);
              }}
            >
              Edit
            </MenuItem>
          )}
          {canEditProject &&
            selectedProject &&
            selectedProject.status !== ProjectStatus.COMPLETED &&
            selectedProject.status !== ProjectStatus.CANCELLED && (
              <MenuItem
                onClick={() => {
                  setCloseConfirmProject(selectedProject);
                  setCardMenuAnchor(null);
                }}
                sx={{ color: 'success.main' }}
              >
                Close Project
              </MenuItem>
            )}
          {canDeleteProject && (
            <MenuItem
              onClick={() => {
                setDeleteDialogOpen(true);
                setCardMenuAnchor(null);
              }}
              sx={{ color: 'error.main' }}
            >
              Delete
            </MenuItem>
          )}
        </Menu>
      )}

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
          sx={{ height: isMobile ? 24 : 28 }}
        />
        <Chip
          label={`${filteredProjects.filter((p: Project) => p.status === ProjectStatus.ACTIVE).length} active`}
          size="small"
          sx={{ bgcolor: 'primary.50', color: 'primary.main', height: isMobile ? 24 : 28 }}
        />
        <Chip
          label={`${filteredProjects.filter((p: Project) => p.status === ProjectStatus.COMPLETED).length} completed`}
          size="small"
          sx={{ bgcolor: 'success.50', color: 'success.main', height: isMobile ? 24 : 28 }}
        />
      </Box>

      {/* Mobile Filter Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        onOpen={() => setMobileFilterOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '60vh',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Handle */}
          <Box
            sx={{
              width: 32,
              height: 4,
              bgcolor: 'grey.300',
              borderRadius: 2,
              mx: 'auto',
              mb: 2,
            }}
          />
          
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Filter Projects
          </Typography>
          
          {/* Status Filter */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            {[
              { value: 'all', label: 'All' },
              { value: ProjectStatus.PLANNING, label: 'Planning' },
              { value: ProjectStatus.ACTIVE, label: 'Active' },
              { value: ProjectStatus.ON_HOLD, label: 'On Hold' },
              { value: ProjectStatus.COMPLETED, label: 'Completed' },
              { value: ProjectStatus.CANCELLED, label: 'Cancelled' },
            ].map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                onClick={() => setStatusFilter(option.value as ProjectStatus | 'all')}
                sx={{
                  bgcolor: statusFilter === option.value ? 'primary.main' : 'grey.100',
                  color: statusFilter === option.value ? 'white' : 'text.primary',
                  '&:hover': {
                    bgcolor: statusFilter === option.value ? 'primary.dark' : 'grey.200',
                  },
                }}
              />
            ))}
          </Box>
          
          {/* Clear / Apply */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setStatusFilter('all');
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setMobileFilterOpen(false)}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* Mobile FAB */}
      {isMobile && canCreateProject && (
        <Fab
          color="primary"
          onClick={handleCreateProject}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}

      {/* Template Modal */}
      <ProjectTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSelectTemplate={(template) => {
          // Navigate to create project with template data
          dispatch(openModal({
            type: 'projectCreate',
            data: {
              template,
              name: `${template.name} Project`,
              estimatedMinutes: template.defaultTasks.reduce((sum, t) => sum + t.estimatedMinutes, 0),
            },
          }));
          setTemplateModalOpen(false);
        }}
      />

      {/* Edit Modal */}
      {selectedProject && (
        <ProjectEditModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}

      {/* Delete Dialog */}
      {selectedProject && (
        <DeleteProjectDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}

      {/* Close Project Confirmation */}
      <ConfirmationModal
        open={Boolean(closeConfirmProject)}
        onClose={() => setCloseConfirmProject(null)}
        data={{
          title: 'Close project',
          message: closeConfirmProject
            ? `Mark "${closeConfirmProject.name}" as completed? This will close the project.`
            : '',
          confirmText: 'Close project',
          cancelText: 'Cancel',
          type: 'success',
          onConfirm: () => {
            if (closeConfirmProject) {
              closeProjectMutation.mutate(closeConfirmProject.id);
            }
          },
        }}
      />
    </Box>
  );
};

export default ProjectsPage;
